import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import DomainManager from './DomainManager';
import UserProfile from './UserProfile';
import AdminPanel from './AdminPanel';
import CrackerController from './CrackerController';
import GrabberDashboardStats from './GrabberDashboardStats';
import GrabberManager from './GrabberManager';
import MyHits from './MyHits';
import GlobalWebSocketManager from './GlobalWebSocketManager';
import LicenseSwitcher from './LicenseSwitcher';
import LicenseInfoBadge from './LicenseInfoBadge';
import AddonPurchaseModal from './AddonPurchaseModal';
import NewLicenseOfferModal from './NewLicenseOfferModal';
import apiService from '../services/api';
import { createLicenseValidator, LicenseInfo } from '../services/licenseValidator';
import { isNewLicense } from '../utils/licenseDisplay';
import { normalizeTelegramUsername } from '../utils/licenseTelegram';
import { emptyGrabberStats, GrabberStatsSnapshot } from '../utils/grabberStats';
import { scannerFetch } from '../utils/scannerFetch';
import { isSuperHit } from '../utils/superHit';

import {
  DashboardIcon,
  DomainIcon,
  StatsIcon,
  GrabberIcon,
  ConfigIcon,
  UserIcon,
  InjectedMarkIcon,
  HitsIcon,
  AdminIcon,
} from './icons/DashboardIcons';
import { InjectedSyringeCloudLogo } from './icons/InjectedSyringeCloudLogo';
import './Dashboard.css';
import './Dashboard.atlas.css';
import './ReconDeck.css';
import './AtlasPageLayout.css';
import './AuthGate.css';

const LICENSE_API = process.env.REACT_APP_LICENSE_API || 'https://glitchcloud.app/license-api';

function getHitXpType(hit: Hit): 'special' | 'valid' | 'invalid' | 'brut' {
  if (isSuperHit(hit as any)) return 'special';
  if (hit.status === 'valid' && hit.verificationResult) return 'valid';
  if (hit.status === 'invalid') return 'invalid';
  return 'brut';
}

interface NavTab {
  id: string;
  name: string;
  shortName: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  requiresLicense?: boolean;
  addonName?: string;
  addonDescription?: string;
  addonColor?: string;
  mobilePrimary?: boolean;
  isNew?: boolean;
  navGroup?: 'core' | 'extensions' | 'system';
}

const NAV_TABS_MAIN: NavTab[] = [
  { id: 'dashboard', name: 'Ops Deck',     shortName: 'Deck',    icon: DashboardIcon, mobilePrimary: true, navGroup: 'core' },
  { id: 'myhits',   name: 'My Hits',      shortName: 'Hits',    icon: HitsIcon,      mobilePrimary: true, navGroup: 'core' },
  { id: 'domains',  name: 'Target Lists', shortName: 'Lists',   icon: DomainIcon,    mobilePrimary: true, navGroup: 'core' },
  { id: 'config',   name: 'Profile',      shortName: 'Profile', icon: ConfigIcon,    mobilePrimary: true, navGroup: 'system' },
  {
    id: 'grabber',
    name: 'Harvest Engine',
    shortName: 'Harvest',
    icon: GrabberIcon,
    requiresLicense: true,
    navGroup: 'extensions',
    addonName: 'Grabber',
    addonDescription: 'Automated domain list generation at scale',
    addonColor: 'from-amber',
  },
];

const ADMIN_TAB: NavTab = {
  id: 'admin',
  name: 'Admin Panel',
  shortName: 'Admin',
  icon: AdminIcon,
  navGroup: 'system',
};

interface User {
  id: string;
  name: string;
  license: string;
  telegramPhotoUrl?: string | null;
}

interface License {
  code: string;
  type: string;
  username?: string;
  expiryDate: string;
  isValid: boolean;
  hwid?: string;
  vpsIp?: string;
  vpsPort?: string;
  vpsIp2?: string;
  vpsPort2?: string;
  vpsIp3?: string;
  vpsPort3?: string;
  vpsList?: { ip: string; port: string }[];
  grabber?: boolean;
  mailer?: boolean;
  debouncer?: boolean;
  stripe?: boolean;
  premiumProxy?: boolean;
  totalScans?: number;
  level?: number;
  currentLevel?: number;
  totalXp?: number;
  currentLevelXp?: number;
  nextLevelXp?: number;
  rank?: string;
  totalScan?: number;
  isAdmin?: boolean;
}

interface Domain {
  id: string;
  url: string;
  addedAt: string;
}

interface ScanResult {
  id: string;
  url: string;
  path: string;
  discoveredAt: string;
  type: string;
  status: string;
  details: any;
}

interface ScanStats {
  totalUrls: number;
  processedUrls: number;
  hits: number;
  validHitsCount?: number;
  invalidHitsCount?: number;
  superHitsCount?: number;
  awsHits: number;
  sendGridHits: number;
  sparkpostHits: number;
  twilioHits: number;
  brevoHits: number;
  smtpHits: number;
  mailgunHits: number;
  mailchimpHits: number;
  postmarkHits: number;
  sendinblueHits: number;
  stripeHits: number;
  otherHits: number;
  // IA — individuel
  openAiHits?: number;
  anthropicHits?: number;
  googleAiHits?: number;
  huggingFaceHits?: number;
  groqHits?: number;
  cohereHits?: number;
  mistralHits?: number;
  startTime: string;
  eta: string;
  isRunning: boolean;
}

interface Hit {
  id: string;
  url: string;
  path: string;
  discoveredAt: string;
  services: string[];
  credentials: any;
  status: 'valid' | 'invalid';
  isNew: boolean;
  verificationResult?: any;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const seenHitIdsRef    = useRef<Set<string>>(new Set());
  const xpInitializedRef = useRef(false);

  const [user, setUser] = useState<User | null>(null);
  const [license, setLicense] = useState<License | null>(null);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [domainsCount, setDomainsCount] = useState<number>(0);
  const [scanStats, setScanStats] = useState<ScanStats | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isGrabbing, setIsGrabbing] = useState(false);
  const [grabberStats, setGrabberStats] = useState<GrabberStatsSnapshot>(emptyGrabberStats());
  const [recentHits, setRecentHits] = useState<Hit[]>([]);
  
  // Nouveaux états pour la gestion des licences
  const [licenseValidator, setLicenseValidator] = useState<any | null>(null);
  const [showLicenseSwitcher, setShowLicenseSwitcher] = useState(false);
  const [, setLicenseValidationStatus] = useState<string>('validating');
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState<{
    name: string;
    description: string;
    color: string;
    addonKey: string;
  } | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNewLicenseModal, setShowNewLicenseModal] = useState(false);
  const hasShownInitialOfferRef = useRef(false);

  const isNewLicenseUser = isNewLicense(license?.type);
  const licenseTelegramUsername = user?.name ? normalizeTelegramUsername(user.name) : '';

  // Initialiser le validateur de licence
  useEffect(() => {
    const initializeLicenseValidator = () => {
      try {
        const validator = createLicenseValidator({
          validationInterval: 60000, // 1 minute
          retryAttempts: 3,
          onLicenseInvalid: (licenseInfo: LicenseInfo) => {
            toast.error('Your license has expired or become invalid. You will be signed out.');
            setLicenseValidationStatus('invalid');
            // Auto sign-out after retry
            setTimeout(() => {
              handleLogout();
            }, 5000);
          },
          onValidationError: (error: Error) => {
            setLicenseValidationStatus('error');
          }
        });
        
        setLicenseValidator(validator);
        
        // Démarrer la validation périodique
        validator.startValidation();
        setLicenseValidationStatus('valid');
        
        // Validate license uniqueness and clean duplicates
        if (!validator.validateLicenseUniqueness()) {
          validator.cleanDuplicateLicenses();
          toast.error('Duplicate licenses detected and cleaned up');
        }

        // Periodic uniqueness check (every 5 minutes)
        const uniquenessCheckInterval = setInterval(() => {
          if (!validator.validateLicenseUniqueness()) {
            validator.cleanDuplicateLicenses();
            toast.error('Duplicate licenses detected and cleaned up');
          }
        }, 300000); // 5 minutes

        // Écouter les événements de changement de licence
        const handleLicenseSwitched = () => {
          // Recharger les données utilisateur après changement de licence
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        };
        
        window.addEventListener('licenseSwitched', handleLicenseSwitched);
        
        return () => {
          validator.stopValidation();
          clearInterval(uniquenessCheckInterval);
          window.removeEventListener('licenseSwitched', handleLicenseSwitched);
        };
      } catch (error) {
        setLicenseValidationStatus('error');
      }
    };
    
    // Initialiser seulement après l'authentification
    if (user && license) {
      initializeLicenseValidator();
    }
  }, [user, license]);

  // Vérifier si l'utilisateur est déjà connecté et charger les données persistées
  useEffect(() => {
    const initializeApp = async () => {
      try {
        const savedUser = localStorage.getItem('license_user');
        const savedLicense = localStorage.getItem('user_license');
        
        if (savedUser && savedLicense) {
          const userData = JSON.parse(savedUser);
          const licenseData = JSON.parse(savedLicense);
          
          // Ensure grabber/mailer props exist on legacy licenses
          if (licenseData.grabber === undefined) {
            licenseData.grabber = false;
          }
          if (licenseData.mailer === undefined) {
            licenseData.mailer = false;
          }
          if (licenseData.premiumProxy === undefined) {
            licenseData.premiumProxy = false;
          }
          // Persist updated license with missing fields
          localStorage.setItem('user_license', JSON.stringify(licenseData));
          
          // Verify the license is still valid
          if (licenseData.isValid) {
            setUser(userData);
            setLicense(licenseData);
            
            // Configure API service with VPS connection info (fallback sur vpsList[0])
            const vpsIp   = licenseData.vpsIp   || licenseData.vpsList?.[0]?.ip   || null;
            const vpsPort = licenseData.vpsPort || licenseData.vpsList?.[0]?.port || null;
            if (vpsIp && vpsPort) {
              apiService.setLicenseKey(licenseData.code, vpsIp, vpsPort);
            }
            
            // Load data in the background without blocking the UI
            loadPersistedData().catch(error => {
              console.error('Error loading persisted data:', error);
            });

            // Silent background refresh — picks up new server-side fields (isAdmin, etc.)
            // without blocking the UI or forcing a re-auth
            const tgId = userData.id?.replace('tg_', '');
            if (tgId) {
              const params = new URLSearchParams({
                telegram_id: tgId,
                username:    userData.name ?? '',
                first_name:  userData.name ?? '',
              });
              fetch(`${LICENSE_API}/auth/telegram-id?${params}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                  if (!data?.valid) return;
                  const merged = {
                    ...licenseData,
                    isAdmin:       data.isAdmin       ?? licenseData.isAdmin       ?? false,
                    rank:          data.rank          ?? licenseData.rank,
                    currentLevel:  data.currentLevel  ?? licenseData.currentLevel,
                    totalXp:       data.totalXp       ?? licenseData.totalXp,
                    currentLevelXp: data.currentLevelXp ?? licenseData.currentLevelXp,
                    nextLevelXp:   data.nextLevelXp   ?? licenseData.nextLevelXp,
                    totalScan:     data.totalScan     ?? licenseData.totalScan,
                    telegramPhotoUrl: data.telegramUser?.photo_url ?? licenseData.telegramPhotoUrl,
                    // VPS fields: always take server version — admin may have added/changed servers
                    vpsIp:   data.vpsIp   ?? licenseData.vpsIp,
                    vpsPort: data.vpsPort ?? licenseData.vpsPort,
                    vpsIp2:  data.vpsIp2  ?? licenseData.vpsIp2,
                    vpsPort2: data.vpsPort2 ?? licenseData.vpsPort2,
                    vpsIp3:  data.vpsIp3  ?? licenseData.vpsIp3,
                    vpsPort3: data.vpsPort3 ?? licenseData.vpsPort3,
                    vpsList: Array.isArray(data.vpsList) && data.vpsList.length > 0
                      ? data.vpsList
                      : licenseData.vpsList,
                  };
                  localStorage.setItem('user_license', JSON.stringify(merged));
                  setLicense(merged);
                  if (data.telegramUser?.photo_url) {
                    setUser(prev => prev ? { ...prev, telegramPhotoUrl: data.telegramUser.photo_url } : prev);
                  }
                })
                .catch(() => { /* network unavailable — ignore */ });
            }
          } else {
            // Expired license — clear local storage
            localStorage.removeItem('license_user');
            localStorage.removeItem('user_license');
          }
        }
      } catch (error) {
        console.error('Initialization error:', error);
        // On error, clear local storage
        localStorage.removeItem('license_user');
        localStorage.removeItem('user_license');
      } finally {
        setIsInitializing(false);
      }
    };

    initializeApp();
  }, []);

  // Popup offre pour les licences « New » (5 s après connexion, tous onglets)
  // Bloqué si le tutoriel est actif
  useEffect(() => {
    if (!isNewLicenseUser || !user || !license?.isValid || hasShownInitialOfferRef.current) return;

    const timer = window.setTimeout(() => {
      if (!hasShownInitialOfferRef.current) {
        setShowNewLicenseModal(true);
        hasShownInitialOfferRef.current = true;
      }
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [isNewLicenseUser, user, license?.isValid]);

  useEffect(() => {
    if (!isNewLicenseUser || showNewLicenseModal) return;

    const timer = window.setTimeout(() => {
      setShowNewLicenseModal(true);
    }, 120000);

    return () => window.clearTimeout(timer);
  }, [isNewLicenseUser, showNewLicenseModal]);

  const loadPersistedData = async () => {
    try {
      // Charger les domains
      try {
        const domainsData = await apiService.getDomains();
        setDomains(domainsData);
      } catch (error) {
        console.error('Erreur chargement domains:', error);
        setDomains([]);
      }

      // Charger le nombre total de domains
      try {
        const count = await apiService.getDomainsCount();
        setDomainsCount(count);
      } catch (error) {
        console.error('Erreur comptage domains:', error);
        setDomainsCount(0);
      }

      // Charger les stats
      try {
        const statsData = await apiService.getScanStatus();
        setScanStats(statsData);
      } catch (error) {
        console.error('Erreur chargement stats:', error);
        setScanStats(null);
      }

      // Charger les résultats
      try {
        const resultsData = await apiService.getScanResults();
        // Ne plus stocker les résultats dans le state
      } catch (error) {
        console.error('Erreur chargement résultats:', error);
      }
    } catch (error) {
      console.error('Erreur chargement données persistées:', error);
    }
  };

  // Fonction pour charger les stats en temps réel
  const loadRealTimeStats = async () => {
    try {
      const realTimeStats = await apiService.getRealTimeStats();
      if (realTimeStats) {
        setScanStats(realTimeStats);
        setIsScanning(realTimeStats.isRunning);
      }
    } catch (error) {
      console.error('Erreur chargement stats temps réel:', error);
    }
  };

  const isLoadingHitsRef = useRef(false);

  // Fonction pour charger les hits récents
  const loadRecentHits = async () => {
    if (isLoadingHitsRef.current) return;
    isLoadingHitsRef.current = true;
    try {
      // Build unified VPS list (same logic as CrackerController)
      const allVps: { ip: string; port: string }[] = [];
      if (license?.vpsList && license.vpsList.length > 0) {
        allVps.push(...license.vpsList);
      } else if (license?.vpsIp && license?.vpsPort) {
        allVps.push({ ip: license.vpsIp, port: license.vpsPort });
      }
      if (license?.vpsIp2 && !allVps.some(v => v.ip === license!.vpsIp2)) {
        allVps.push({ ip: license.vpsIp2, port: license.vpsPort2 || '3007' });
      }
      if (allVps.length === 0) { isLoadingHitsRef.current = false; return; }

      // Fetch from all VPS concurrently — 6s timeout each to prevent connection pool starvation
      const results = await Promise.allSettled(
        allVps.map(vps => {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 6000);
          return scannerFetch(vps.ip, vps.port, '/api/hits?type=all', { signal: ctrl.signal })
            .then(r => { clearTimeout(timer); return r.ok ? r.json() : Promise.resolve(null); })
            .catch(() => { clearTimeout(timer); return null; });
        })
      );

      // Merge + deduplicate by ID
      const seenIds = new Set<string>();
      const hits: Hit[] = [];
      for (const r of results) {
        if (r.status !== 'fulfilled' || !r.value) continue;
        const batch: Hit[] = Array.isArray(r.value) ? r.value : (r.value?.hits ?? []);
        for (const h of batch) {
          if (!seenIds.has(h.id)) {
            seenIds.add(h.id);
            hits.push(h);
          }
        }
      }

      setRecentHits(hits);

      if (!xpInitializedRef.current) {
          // Premier chargement : marquer tous les hits existants sans donner de XP
          hits.forEach(h => seenHitIdsRef.current.add(h.id));
          xpInitializedRef.current = true;
        } else {
          // Hits suivants : XP pour chaque nouveau hit
          const jwt = (license as any)?.jwt;
          const licenseCode = license?.code;
          if (jwt && licenseCode) {
            const newHits = hits.filter(h => !seenHitIdsRef.current.has(h.id));
            newHits.forEach(h => seenHitIdsRef.current.add(h.id));
            let lastXpResponse: any = null;
            for (const hit of newHits) {
              const hitType = getHitXpType(hit);
              if (hitType === 'brut') continue;
              try {
                const xpRes = await fetch(`${LICENSE_API}/xp/gain`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jwt}`,
                  },
                  body: JSON.stringify({ license: licenseCode, hitType }),
                });
                if (xpRes.ok) lastXpResponse = await xpRes.json();
              } catch { /* offline — silencieux */ }
            }
            if (lastXpResponse?.ok) {
              setLicense(prev => {
                if (!prev) return prev;
                const updated = {
                  ...prev,
                  currentLevel:    lastXpResponse.currentLevel    ?? prev.currentLevel,
                  totalXp:         lastXpResponse.totalXp         ?? prev.totalXp,
                  currentLevelXp:  lastXpResponse.currentLevelXp  ?? prev.currentLevelXp,
                  nextLevelXp:     lastXpResponse.nextLevelXp      ?? prev.nextLevelXp,
                  rank:            lastXpResponse.rank             ?? prev.rank,
                };
                localStorage.setItem('user_license', JSON.stringify(updated));
                return updated;
              });
            }
          }
        }
    } catch (error) {
      console.error('Erreur chargement hits récents:', error);
    } finally {
      isLoadingHitsRef.current = false;
    }
  };

  // Fonction pour obtenir l'icône d'un service
  const getServiceIcon = (service: string) => {
    const icons: Record<string, string> = {
      'AWS': '◈',
      'SendGrid': '✉',
      'SMTP': '✉',
      'Twilio': '◉',
      'Stripe': '⬡',
      'MailGun': '◈',
      'MailChimp': '◉',
      'Brevo': '✉',
      'SparkPost': '◈',
      'Postmark': '✉',
      'SendinBlue': '✉'
    };
    return icons[service] || '◈';
  };

  // Handlers pour le gestionnaire de licences
  const handleAddLicense = () => {
    // Le modal d'ajout est géré directement dans LicenseSwitcher
  };

  const handleSwitchLicense = (licenseData: any) => {
    toast.success(`Switched to license: ${licenseData.username}`);
  };

  // Fonction pour vérifier le statut du grabber
  const checkGrabberStatus = async () => {
    if (!license || !license.vpsIp || !license.vpsPort || !license.grabber) return;
    
    try {
      const response = await scannerFetch(license.vpsIp!, license.vpsPort!, '/grabber-stats');
      
      if (response.ok) {
        const data = await response.json();
        setIsGrabbing(data.isGrabbing);
        if (data.stats) {
          setGrabberStats(data.stats);
        } else if (!data.isGrabbing) {
          setGrabberStats(emptyGrabberStats());
        }
      }
    } catch (error) {
      console.error('Erreur vérification statut grabber:', error);
    }
  };

  // Mettre à jour les stats en temps réel quand l'utilisateur est connecté
  useEffect(() => {
    if (user && license) {
      loadRealTimeStats(); // immediate call on mount to restore scanning state
      const interval = setInterval(() => {
        loadRealTimeStats();
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [user, license]);

  // Vérifier le statut du grabber quand l'utilisateur a accès au grabber
  useEffect(() => {
    if (user && license && license.grabber) {
      const interval = setInterval(() => {
        checkGrabberStatus();
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [user, license]);


  // Rafraîchir les données XP/totalScan depuis le serveur toutes les 60s
  useEffect(() => {
    if (!user || !license) return;
    const refreshLicenseXp = async () => {
      try {
        const res = await fetch(`${LICENSE_API}/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ license: license.code, hwid: license.hwid || '' }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!data.valid) return;
        setLicense(prev => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            currentLevel:   data.currentLevel   ?? prev.currentLevel,
            totalXp:        data.totalXp        ?? prev.totalXp,
            currentLevelXp: data.currentLevelXp ?? prev.currentLevelXp,
            nextLevelXp:    data.nextLevelXp    ?? prev.nextLevelXp,
            rank:           data.rank           ?? prev.rank,
            totalScan:      data.totalScan      ?? prev.totalScan,
          };
          localStorage.setItem('user_license', JSON.stringify(updated));
          return updated;
        });
      } catch { /* offline */ }
    };
    refreshLicenseXp();
    const interval = setInterval(refreshLicenseXp, 60000);
    return () => clearInterval(interval);
  }, [user?.id, license?.code]); // only re-run when user/license identity changes

  // Charger les hits récents quand l'utilisateur est connecté
  useEffect(() => {
    if (user && license) {
      loadRecentHits();
      const interval = setInterval(() => {
        loadRecentHits();
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [user, license]);

  const handleLicenseActivation = async (licenseData: License) => {
    try {
      // Créer un utilisateur basé sur la licence
      const userData: User = {
        id: licenseData.hwid || 'unknown',
        name: licenseData.username || `Utilisateur ${licenseData.type}`,
        license: licenseData.code
      };
      
      setUser(userData);
      setLicense(licenseData);
      
      // Configurer l'API service avec les infos VPS
      if (licenseData.vpsIp && licenseData.vpsPort) {
        apiService.setLicenseKey(licenseData.code, licenseData.vpsIp, licenseData.vpsPort);
      }
      
      // Sauvegarder dans localStorage
      localStorage.setItem('license_user', JSON.stringify(userData));
      localStorage.setItem('user_license', JSON.stringify(licenseData));
      
      // Charger les données après activation de la licence
      await loadPersistedData();
    } catch (error) {
      console.error('Erreur activation licence:', error);
      alert('Could not activate your license. Please try again.');
    }
  };

  const handleLogout = () => {
    setUser(null);
    setLicense(null);
    localStorage.removeItem('license_user');
    localStorage.removeItem('user_license');
    navigate('/');
  };

  const NAV_TABS = useMemo(
    () => license?.isAdmin ? [...NAV_TABS_MAIN, ADMIN_TAB] : NAV_TABS_MAIN,
    [license?.isAdmin]
  );

  const activeTabMeta = useMemo(
    () => NAV_TABS.find((t) => t.id === activeTab) ?? NAV_TABS[0],
    [activeTab, NAV_TABS]
  );

  const hasTabAccess = (tab: NavTab) => {
    if (!tab.requiresLicense) return true;
    if (tab.id === 'grabber') return !!license?.grabber;
    if (tab.id === 'mailer') return !!license?.mailer;
    if (tab.id === 'debouncer') return !!license?.debouncer;
    if (tab.id === 'stripe') return !!license?.stripe;
    return false;
  };

  const handleTabSelect = (tab: NavTab) => {
    if (tab.requiresLicense && !hasTabAccess(tab)) {
      setSelectedAddon({
        name: tab.addonName || tab.name,
        description: tab.addonDescription || '',
        color: tab.addonColor || 'from-amber',
        addonKey: tab.id,
      });
      setShowAddonModal(true);
      return;
    }
    setActiveTab(tab.id);
    setShowMobileMenu(false);
  };

  const renderLockedAddonPreview = (
    children: React.ReactNode,
    tab: NavTab
  ) => (
    <div className="dashboard-addon-preview">
      <div className="dashboard-addon-preview-content" aria-hidden="true">
        {children}
      </div>
      <div className="dashboard-addon-preview-lock">
        <div className="dashboard-addon-preview-lock-inner">
          <div className="dashboard-addon-preview-lock-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
              <path
                d="M7 11V8a5 5 0 0110 0v3M6 11h12v9H6v-9z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="dashboard-addon-preview-lock-title">{tab.addonName || tab.name} add-on</p>
          <p className="dashboard-addon-preview-lock-desc">
            Preview only — activate this add-on to use it
          </p>
          {(
            <button
              type="button"
              onClick={() => {
                setSelectedAddon({
                  name: tab.addonName || tab.name,
                  description: tab.addonDescription || '',
                  color: tab.addonColor || 'from-amber',
                  addonKey: tab.id,
                });
                setShowAddonModal(true);
              }}
              className="dashboard-btn dashboard-btn--gold dashboard-addon-preview-lock-btn"
            >
              Activate {tab.addonName || tab.name}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderNavButton = (
    tab: NavTab,
    variant: 'sidebar' | 'rail' | 'workspace' = 'sidebar',
    label?: string
  ) => {
    const isActive = activeTab === tab.id;
    const locked = tab.requiresLicense && license && !hasTabAccess(tab);

    const variantClass =
      variant === 'rail'
        ? 'dashboard-rail-btn'
        : variant === 'workspace'
          ? 'dashboard-workspace-tab'
          : 'dashboard-nav-item';

    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => handleTabSelect(tab)}
        data-tour={`tab-${tab.id}`}
        className={`${variantClass} ${isActive ? `${variantClass}--active` : ''} ${
          tab.isNew ? 'dashboard-nav-item--new' : ''
        }`}
        title={tab.name}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className={variant === 'workspace' ? 'dashboard-workspace-tab-icon' : 'dashboard-nav-icon'}>
          <tab.icon size={variant === 'rail' ? 18 : 14} />
        </span>
        {variant === 'rail' && <span className="dashboard-rail-btn-label">{tab.shortName}</span>}
        {variant === 'workspace' && (
          <>
            <span className="dashboard-workspace-tab-text">{tab.shortName}</span>
            {tab.isNew && !locked && <span className="dashboard-badge-new">New</span>}
            {locked && <span className="dashboard-badge-premium">Lock</span>}
          </>
        )}
        {variant === 'sidebar' && (
          <>
            <span className="flex-1 text-left truncate">{label || tab.name}</span>
            {tab.isNew && !locked && <span className="dashboard-badge-new">New</span>}
            {locked && <span className="dashboard-badge-premium">Lock</span>}
          </>
        )}
      </button>
    );
  };

  const renderWorkspaceNav = () => (
    <div className="dashboard-workspace-nav-inner">
      <div className="dashboard-workspace-group">
        <span className="dashboard-workspace-group-label">Core</span>
        <div className="dashboard-workspace-tabs">
          {NAV_TABS.filter((t) => t.navGroup === 'core').map((tab) => renderNavButton(tab, 'workspace'))}
        </div>
      </div>
      <div className="dashboard-workspace-group">
        <span className="dashboard-workspace-group-label">Extensions</span>
        <div className="dashboard-workspace-tabs">
          {NAV_TABS.filter((t) => t.navGroup === 'extensions').map((tab) => renderNavButton(tab, 'workspace'))}
        </div>
      </div>
      <div className="dashboard-workspace-group">
        <span className="dashboard-workspace-group-label">System</span>
        <div className="dashboard-workspace-tabs">
          {NAV_TABS.filter((t) => t.navGroup === 'system').map((tab) => renderNavButton(tab, 'workspace'))}
        </div>
      </div>
    </div>
  );

  const renderSidebarNav = () => (
    <>
      {NAV_TABS.map((tab) => renderNavButton(tab, 'sidebar'))}
    </>
  );

  // Écran de chargement initial
  if (isInitializing) {
    return (
      <div className="dashboard-shell dashboard-init-hud dashboard-grid-bg flex items-center justify-center min-h-screen min-h-[100dvh] relative">
        <div className="dashboard-scanline absolute inset-0" />
        <div className="relative z-10 text-center px-6">
          <div className="inline-flex items-center justify-center w-14 h-14 mb-6 rounded-md border border-[#c62828]/30 bg-[#1e1e1e] text-[#e53935]">
            <InjectedMarkIcon size={28} />
          </div>
          <p className="text-[0.625rem] font-semibold tracking-[0.14em] uppercase text-[#c62828] mb-2 font-mono">
            SYS.INIT
          </p>
          <h1 className="text-xl font-semibold text-white mb-1 tracking-tight">Injected</h1>
          <p className="text-sm text-[#737373] mb-8 font-mono">Booting platform services…</p>
          <div className="dashboard-init-ring mx-auto" />
        </div>
      </div>
    );
  }

  // Accès uniquement via Telegram MiniApp
  if (!user || !license || !license.isValid) {
    return (
      <div className="auth-gate">
        <div className="auth-gate-visual">
          <div className="auth-gate-visual-inner">
            <div className="auth-gate-logo-wrap">
              <InjectedSyringeCloudLogo variant="hero" />
            </div>
            <p className="auth-gate-kicker">Injected Atlas</p>
            <h1 className="auth-gate-title">
              Accès via <em>Telegram uniquement</em>
            </h1>
            <p className="auth-gate-lead">
              Cette interface est exclusivement accessible depuis la Mini-App Telegram.
              Ouvre le bot et lance la Mini-App depuis ton compte.
            </p>
          </div>
        </div>
        <div className="auth-gate-panel-wrap">
          <div className="auth-gate-panel" style={{ textAlign: 'center' }}>
            <div className="flex items-center justify-center gap-2 mb-6 text-[#e53935]">
              <InjectedMarkIcon size={28} />
            </div>
            <h2 className="auth-gate-form-title" style={{ marginBottom: 8 }}>Accès restreint</h2>
            <p className="auth-gate-form-sub" style={{ marginBottom: 28 }}>
              L'accès à l'interface est uniquement disponible via la Mini-App Telegram.
            </p>
            <a
              href="https://t.me/InjectedScannerBot"
              target="_blank"
              rel="noopener noreferrer"
              className="cracker-btn cracker-btn--gold"
              style={{ display: 'inline-block' }}
            >
              📱 Ouvrir le bot Telegram
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-shell dashboard-shell--atlas dashboard-shell--deck dashboard-mesh-bg flex h-screen h-[100dvh] overflow-hidden relative">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e1e1e',
            color: '#fff',
            border: '1px solid rgba(198, 40, 40, 0.2)',
          },
        }}
      />

      <aside className="deck-sidebar recon-side hidden md:flex" aria-label="Workspace modules">
        <div className="recon-brand">
          <span className="recon-brand-mark">IN</span>
          <div>
            <p className="recon-brand-name">Injected</p>
            <p className="recon-brand-badge">Scanner</p>
          </div>
        </div>
        <button type="button" className="recon-user" onClick={() => setShowLicenseSwitcher(true)}>
          {user.telegramPhotoUrl ? (
            <img
              src={user.telegramPhotoUrl}
              alt=""
              className="recon-avatar"
              style={{ objectFit: 'cover', borderRadius: '50%', padding: 0 }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                const next = e.currentTarget.nextElementSibling as HTMLElement | null;
                if (next) next.style.display = 'flex';
              }}
            />
          ) : null}
          <span
            className="recon-avatar"
            style={user.telegramPhotoUrl ? { display: 'none' } : undefined}
          >
            {(user.name || 'OP').slice(0, 2).toUpperCase()}
          </span>
          <span>
            <span className="recon-user-name">@{user.name}</span>
            <span className="recon-user-level">{license.type}</span>
          </span>
        </button>
        <nav className="deck-nav recon-nav">
          <p className="recon-nav-label">Scans</p>
          {renderNavButton(NAV_TABS.find((t) => t.id === 'dashboard')!, 'sidebar', 'My Scans')}
          {renderNavButton(NAV_TABS.find((t) => t.id === 'myhits')!, 'sidebar', 'My Hits')}
          {renderNavButton(NAV_TABS.find((t) => t.id === 'domains')!, 'sidebar', 'Targets')}
          <p className="recon-nav-label">Account</p>
          {renderNavButton(NAV_TABS.find((t) => t.id === 'config')!, 'sidebar', 'Profile')}
          {license?.isAdmin && renderNavButton(ADMIN_TAB, 'sidebar', 'Admin Panel')}
          <p className="recon-nav-label">Extensions</p>
          {renderNavButton(NAV_TABS.find((t) => t.id === 'grabber')!, 'sidebar', 'Harvest')}
        </nav>
        <div className="recon-live">
          <span className={`recon-live-dot${license?.vpsIp ? ' recon-live-dot--on' : ''}`} />
          {license?.vpsIp ? 'live connected' : 'offline'}
        </div>
        <button type="button" className="deck-exit" onClick={handleLogout}>
          Exit
        </button>
      </aside>

      {/* Main column */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        <header className="deck-topbar md:hidden">
          <div className="flex items-center gap-2 min-w-0">
            {user.telegramPhotoUrl ? (
              <img
                src={user.telegramPhotoUrl}
                alt=""
                style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1.5px solid rgba(232,19,46,0.45)' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <InjectedMarkIcon size={16} />
            )}
            <div className="min-w-0">
              <p className="deck-topbar-kicker">Injected Atlas</p>
              <p className="deck-topbar-title">{activeTabMeta.name}</p>
            </div>
          </div>
          <button
            type="button"
            className="dashboard-btn dashboard-btn--ghost"
            onClick={() => setShowMobileMenu(true)}
            aria-label="Menu navigation"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </header>

        {/* Content */}
        <main className="dashboard-main dashboard-main--fluid flex-1 pb-[4.75rem] md:pb-4">
          <div className="dashboard-content-inner dashboard-content-inner--wide">
            <AnimatePresence mode="wait">
              {activeTab === 'dashboard' && (
                <motion.div
                  key="dashboard"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {license.grabber && isGrabbing && (
                    <GrabberDashboardStats stats={grabberStats} isGrabbing={isGrabbing} />
                  )}
                  <CrackerController
                    vpsIp={license.vpsIp}
                    vpsPort={license.vpsPort}
                    vpsIp2={license.vpsIp2}
                    vpsPort2={license.vpsPort2}
                    vpsIp3={license.vpsIp3}
                    vpsPort3={license.vpsPort3}
                    vpsList={license.vpsList}
                    isGrabbing={isGrabbing}
                    user={user}
                    license={license}
                    isScanning={isScanning}
                    recentHits={recentHits}
                    domainsCount={domainsCount}
                    setActiveTab={setActiveTab}
                  />
                </motion.div>
              )}

              {activeTab === 'domains' && (
                <motion.div
                  key="domains"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <DomainManager
                    domains={domains}
                    onDomainsChange={setDomains}
                    onDomainsCountChange={setDomainsCount}
                    isLoading={isLoading}
                    licenseType={license?.type}
                    licenseExpiry={license?.expiryDate}
                    licenseGrabber={license?.grabber}
                    licenseMailer={license?.mailer}
                    licenseUsername={user?.name}
                    vpsIp={license?.vpsIp}
                    vpsPort={license?.vpsPort}
                    vpsIp2={license?.vpsIp2}
                    vpsPort2={license?.vpsPort2}
                    vpsList={license?.vpsList}
                  />
                </motion.div>
              )}

              {activeTab === 'config' && (
                <motion.div
                  key="config"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <UserProfile
                    user={user}
                    license={license}
                    isScanning={isScanning}
                  />
                </motion.div>
              )}

              {activeTab === 'myhits' && (
                <motion.div
                  key="myhits"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <MyHits
                    vpsConfig={{ ip: license.vpsIp || '', port: license.vpsPort || '' }}
                    setActiveTab={setActiveTab}
                  />
                </motion.div>
              )}

              {activeTab === 'grabber' && (
                <motion.div
                  key="grabber"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  {license.grabber ? (
                    <GrabberManager
                      licenseData={license}
                      vpsConfig={{ ip: license.vpsIp, port: license.vpsPort }}
                      isScanning={isScanning}
                    />
                  ) : (
                    renderLockedAddonPreview(
                      <GrabberManager
                        previewMode
                        licenseData={license}
                        vpsConfig={{ ip: license.vpsIp || '0.0.0.0', port: license.vpsPort || '0' }}
                        isScanning={isScanning}
                      />,
                      NAV_TABS.find((t) => t.id === 'grabber')!
                    )
                  )}
                </motion.div>
              )}

              {activeTab === 'admin' && license?.isAdmin && (
                <motion.div
                  key="admin"
                  initial={{ opacity: 0, y: 10, scale: 0.996 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -6, scale: 0.998 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <AdminPanel />
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Mobile bottom navigation — liquid bulge */}
      {(() => {
        const primaryTabs = NAV_TABS.filter((t) => t.mobilePrimary);
        const activeIdx   = primaryTabs.findIndex((t) => t.id === activeTab);
        const blobIdx     = activeIdx >= 0 ? activeIdx : 0;
        const tabW        = 100 / primaryTabs.length;
        return (
          <div className="lnav-root md:hidden">
            {/* Hidden SVG gooey filter */}
            <svg className="lnav-filter-svg" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="lnav-goo" x="-20%" y="-60%" width="140%" height="220%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="11" result="blur" />
                  <feColorMatrix in="blur" mode="matrix"
                    values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 28 -12" />
                </filter>
              </defs>
            </svg>
            {/* Gooey layer: background + one blob per tab (opacity-driven) */}
            <div className="lnav-goo-wrap" style={{ filter: 'url(#lnav-goo)' }}>
              <div className="lnav-bg" />
              {primaryTabs.map((_, idx) => (
                <div
                  key={idx}
                  className={`lnav-blob${idx === blobIdx ? ' lnav-blob--active' : ''}`}
                  style={{ left: `calc(${idx * tabW}% + ${tabW / 2}%)` }}
                />
              ))}
            </div>
            {/* Tab buttons */}
            <div className="lnav-tabs">
              {primaryTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleTabSelect(tab)}
                    className={`lnav-tab${isActive ? ' lnav-tab--active' : ''}`}
                  >
                    <span className="lnav-tab-icon">
                      {tab.id === 'config' ? (
                        user?.telegramPhotoUrl ? (
                          <img
                            src={user.telegramPhotoUrl}
                            className="lnav-avatar"
                            alt=""
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <span className="lnav-avatar-fallback">
                            {(user?.name || '?').slice(0, 1).toUpperCase()}
                          </span>
                        )
                      ) : (
                        <tab.icon size={20} />
                      )}
                    </span>
                    <span className="lnav-tab-label">{tab.shortName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Mobile navigation drawer */}
      <AnimatePresence>
        {showMobileMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="dashboard-modal-overlay fixed inset-0 z-40 md:hidden"
              onClick={() => setShowMobileMenu(false)}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 340, damping: 32 }}
              className="mdrawer md:hidden"
            >
              {/* Header — user identity */}
              <div className="mdrawer-header">
                <div className="mdrawer-user">
                  {user?.telegramPhotoUrl ? (
                    <img src={user.telegramPhotoUrl} className="mdrawer-avatar" alt="" />
                  ) : (
                    <span className="mdrawer-avatar mdrawer-avatar--fallback">
                      {(user?.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="mdrawer-user-info">
                    <span className="mdrawer-username">@{user?.name || 'Operator'}</span>
                    <span className="mdrawer-rank">{(license?.rank || 'Ghosted').toUpperCase()}</span>
                  </div>
                </div>
                <button type="button" className="mdrawer-close" onClick={() => setShowMobileMenu(false)} aria-label="Close">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <line x1="1" y1="1" x2="13" y2="13"/><line x1="13" y1="1" x2="1" y2="13"/>
                  </svg>
                </button>
              </div>

              {/* Nav */}
              <nav className="mdrawer-nav dashboard-sidebar-scroll">
                <p className="mdrawer-section-label">Core</p>
                {NAV_TABS_MAIN.map((tab) => renderNavButton(tab, 'sidebar'))}
                <p className="mdrawer-section-label">Extensions</p>
                {NAV_TABS.filter((t) => t.navGroup === 'extensions').map((tab) => renderNavButton(tab, 'sidebar'))}
                {license?.isAdmin && (
                  <>
                    <p className="mdrawer-section-label">Admin</p>
                    {renderNavButton(ADMIN_TAB, 'sidebar')}
                  </>
                )}
              </nav>

              {/* Footer */}
              <div className="mdrawer-footer">
                <button type="button" className="mdrawer-signout" onClick={handleLogout}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <GlobalWebSocketManager />

      {/* License Switcher Modal */}
      <AnimatePresence>
        {showLicenseSwitcher && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="dashboard-modal-overlay fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLicenseSwitcher(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <LicenseSwitcher
                embedded
                isOpen={showLicenseSwitcher}
                onClose={() => setShowLicenseSwitcher(false)}
                onSwitchLicense={handleSwitchLicense}
                onAddLicense={handleAddLicense}
                currentLicense={{
                  id: user?.id || '',
                  code: license?.code || '',
                  type: license?.type || '',
                  username: user?.name || '',
                  expiryDate: license?.expiryDate || '',
                  isValid: license?.isValid || false,
                  isActive: true,
                  hwid: license?.grabber ? license.hwid : undefined,
                  vpsIp: license?.grabber ? license.vpsIp : undefined,
                  vpsPort: license?.grabber ? license.vpsPort : undefined,
                  grabber: license?.grabber || false,
                  mailer: license?.mailer || false,
                  debouncer: license?.debouncer || false,
                  stripe: license?.stripe || false,
                  premiumProxy: license?.premiumProxy || false,
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {selectedAddon && (
        <AddonPurchaseModal
          isOpen={showAddonModal}
          onClose={() => {
            setShowAddonModal(false);
            setSelectedAddon(null);
          }}
          addonName={selectedAddon.name}
          addonDescription={selectedAddon.description}
          addonColor={selectedAddon.color}
          addonKey={selectedAddon.addonKey}
          telegramUsername={licenseTelegramUsername}
          onSuccess={() => {
            toast.success(`${selectedAddon.name} module activated successfully.`);
            setTimeout(() => {
              window.location.reload();
            }, 2000);
          }}
        />
      )}

      {/* Popup offre bloqué pendant le tutoriel */}
      <NewLicenseOfferModal
        isOpen={showNewLicenseModal}
        onClose={() => setShowNewLicenseModal(false)}
        telegramUsername={licenseTelegramUsername}
      />
    </div>
  );
};

export default Dashboard; 